import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { sortRegions } from '../utils/sort';
import PageLayout from '../components/PageLayout';
import {
  fetchRegionBackgroundCdnOptions,
  REGION_BACKGROUND_CDN_FOLDER,
  type RegionBackgroundOption,
} from '../utils/regionBackgroundCdn';
import { fetchMapMarkerCdnOptions, type MapMarkerOption } from '../utils/mapMarkerCdn';
import { regionAssetPayload } from '../utils/regionPayload';
import './RegionsPage.css';

interface Region {
  region_name: string;
  metro_name: string;
  /** Two-letter state used in city-page SEO copy (e.g. IL) */
  state_code?: string;
  latitude: number;
  longitude: number;
  radius: number;
  unit: string; // "miles" or "km"
  default_zoom?: number;
  default_pitch?: number;
  default_bearing?: number;
  zip_code?: string;
  is_visible: boolean;
  /** CDN-relative "{region_name}/{file}" under region-backgrounds/ */
  region_background?: string;
  /** CDN-relative "{region_name}/{file}" under map-markers/ for venues without a custom marker */
  default_map_marker?: string;
}

const RegionsPage: React.FC = () => {
  const { token } = useAuth();
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [formData, setFormData] = useState<Partial<Region> & { latitudeInput?: string, longitudeInput?: string }>({
    unit: 'miles',
  });
  const [backgroundOptions, setBackgroundOptions] = useState<RegionBackgroundOption[]>([]);
  const [loadingBackgrounds, setLoadingBackgrounds] = useState(false);
  const [markerOptions, setMarkerOptions] = useState<MapMarkerOption[]>([]);

  useEffect(() => {
    if (token) {
      loadRegions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token || !showForm) return;
    const slug =
      (formData.region_name && formData.region_name.trim()) ||
      (editingRegion?.region_name && editingRegion.region_name.trim()) ||
      '';
    if (!slug) {
      setBackgroundOptions([]);
      setMarkerOptions([]);
      return;
    }
    let cancelled = false;
    setLoadingBackgrounds(true);
    (async () => {
      try {
        const [opts, markers] = await Promise.all([
          fetchRegionBackgroundCdnOptions(token, slug),
          fetchMapMarkerCdnOptions(token, slug).catch((err) => {
            console.error('Failed to load region map markers:', err);
            return { shared: [], regional: [] };
          }),
        ]);
        if (!cancelled) {
          setBackgroundOptions(opts);
          setMarkerOptions(markers.regional);
        }
      } catch (err) {
        console.error('Failed to load region backgrounds:', err);
        if (!cancelled) {
          setBackgroundOptions([]);
          setMarkerOptions([]);
        }
      } finally {
        if (!cancelled) setLoadingBackgrounds(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, showForm, formData.region_name, editingRegion?.region_name]);

  const loadRegions = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await apiRequest<{ regions: Region[]; count: number }>(
        '/admin/regions',
        token
      );
      setRegions(sortRegions(response.regions || []));
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load regions');
      setRegions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRegion(null);
    setFormData({
      unit: 'miles',
      is_visible: false,
      latitudeInput: '',
      longitudeInput: ''
    });
    setShowForm(true);
  };

  const handleEdit = (region: Region) => {
    setEditingRegion(region);
    setFormData({
      ...region,
      is_visible: region.is_visible ?? false,
      latitudeInput: region.latitude.toString(),
      longitudeInput: region.longitude.toString()
    });
    setShowForm(true);
  };

  const handleDelete = async (regionName: string) => {
    if (!token || !window.confirm(`Are you sure you want to delete the region "${regionName}"?`)) {
      return;
    }
    try {
      await apiRequest(`/admin/regions/${regionName}`, token, { method: 'DELETE' });
      await loadRegions();
    } catch (err: any) {
      setError(err.message || 'Failed to delete region');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      const payload: Record<string, unknown> = {
        region_name: formData.region_name || '',
        metro_name: formData.metro_name || '',
        state_code: (formData.state_code || '').trim().toUpperCase(),
        latitude: formData.latitude ? parseFloat(formData.latitude.toString()) : 0,
        longitude: formData.longitude ? parseFloat(formData.longitude.toString()) : 0,
        radius: formData.radius ? parseInt(formData.radius.toString(), 10) : 0,
        unit: formData.unit || 'miles',
        is_visible: !!formData.is_visible,
      };

      if (formData.default_zoom !== undefined && formData.default_zoom !== null) {
        const parsedDefaultZoom = parseFloat(formData.default_zoom.toString());
        if (!Number.isNaN(parsedDefaultZoom)) {
          payload.default_zoom = parsedDefaultZoom;
        }
      }

      if (formData.default_pitch !== undefined && formData.default_pitch !== null) {
        const parsed = parseFloat(formData.default_pitch.toString());
        if (!Number.isNaN(parsed)) {
          payload.default_pitch = parsed;
        }
      }

      if (formData.default_bearing !== undefined && formData.default_bearing !== null) {
        const parsed = parseFloat(formData.default_bearing.toString());
        if (!Number.isNaN(parsed)) {
          payload.default_bearing = parsed;
        }
      }

      if (formData.zip_code) {
        payload.zip_code = formData.zip_code;
      }

      Object.assign(payload, regionAssetPayload(formData));

      if (editingRegion) {
        await apiRequest(`/admin/regions/${editingRegion.region_name}`, token, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiRequest('/admin/regions', token, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setShowForm(false);
      await loadRegions();
    } catch (err: any) {
      setError(err.message || 'Failed to save region');
    }
  };

  if (loading) {
    return (
      <PageLayout title="Regions">
        <div>Loading...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Regions">
      {error && <div className="error-banner">{error}</div>}

      <div className="regions-controls">
        <button onClick={handleCreate} className="btn-primary">
          Create Region
        </button>
      </div>

      <div className="regions-list">
        {regions.length === 0 ? (
          <div className="empty-state">No regions found. Create one to get started.</div>
        ) : (
          <table className="regions-table">
            <thead>
              <tr>
                <th>Region Name</th>
                <th>Metro Name</th>
                <th>State</th>
                <th>Visible</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Radius</th>
                <th>Unit</th>
                <th>Default Zoom</th>
                <th>Pitch</th>
                <th>Bearing</th>
                <th>ZIP Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {regions.map((region) => (
                <tr key={region.region_name}>
                  <td>{region.region_name}</td>
                  <td>{region.metro_name}</td>
                  <td>{region.state_code || '-'}</td>
                  <td>{region.is_visible ? 'Yes' : 'No'}</td>
                  <td>{region.latitude.toFixed(6)}</td>
                  <td>{region.longitude.toFixed(6)}</td>
                  <td>{region.radius}</td>
                  <td>{region.unit}</td>
                  <td>{typeof region.default_zoom === 'number' ? region.default_zoom.toFixed(2) : '-'}</td>
                  <td>{typeof region.default_pitch === 'number' ? region.default_pitch.toFixed(2) : '-'}</td>
                  <td>{typeof region.default_bearing === 'number' ? region.default_bearing.toFixed(2) : '-'}</td>
                  <td>{region.zip_code || '-'}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(region)}
                      className="btn-secondary btn-small"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(region.region_name)}
                      className="btn-danger btn-small"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRegion ? 'Edit Region' : 'Create Region'}</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>
                  Region Name {editingRegion ? '(read-only)' : '*'}
                </label>
                <input
                  type="text"
                  value={formData.region_name || ''}
                  onChange={(e) => setFormData({ ...formData, region_name: e.target.value })}
                  required={!editingRegion}
                  disabled={!!editingRegion}
                  placeholder="e.g., chicago"
                />
              </div>
              <div className="form-group">
                <label>Metro Name *</label>
                <input
                  type="text"
                  value={formData.metro_name || ''}
                  onChange={(e) => setFormData({ ...formData, metro_name: e.target.value })}
                  required
                  placeholder="e.g., Chicago"
                />
              </div>
              <div className="form-group">
                <label>State Code (two letters, used in city-page SEO)</label>
                <input
                  type="text"
                  value={formData.state_code || ''}
                  onChange={(e) => setFormData({ ...formData, state_code: e.target.value.toUpperCase() })}
                  maxLength={2}
                  pattern="[A-Za-z]{2}"
                  placeholder="e.g., IL"
                />
              </div>
              <div className="form-group form-group-switch">
                <span id="region-visible-label" className="switch-label">
                  Visible on public map
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!formData.is_visible}
                  aria-labelledby="region-visible-label"
                  className={`region-visibility-switch ${formData.is_visible ? 'on' : ''}`}
                  onClick={() => setFormData({ ...formData, is_visible: !formData.is_visible })}
                >
                  <span className="region-visibility-knob" />
                </button>
              </div>
              <div className="form-group">
                <label>Latitude *</label>
                <input
                  type="text"
                  value={formData.latitudeInput !== undefined ? formData.latitudeInput : (formData.latitude || '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    const num = parseFloat(val);
                    setFormData({ 
                      ...formData, 
                      latitudeInput: val,
                      latitude: isNaN(num) ? 0 : num 
                    });
                  }}
                  required
                  placeholder="e.g., 41.8781"
                />
              </div>
              <div className="form-group">
                <label>Longitude *</label>
                <input
                  type="text"
                  value={formData.longitudeInput !== undefined ? formData.longitudeInput : (formData.longitude || '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    const num = parseFloat(val);
                    setFormData({ 
                      ...formData, 
                      longitudeInput: val,
                      longitude: isNaN(num) ? 0 : num 
                    });
                  }}
                  required
                  placeholder="e.g., -87.6298"
                />
              </div>
              <div className="form-group">
                <label>Radius *</label>
                <input
                  type="number"
                  value={formData.radius || ''}
                  onChange={(e) => setFormData({ ...formData, radius: parseInt(e.target.value) || 0 })}
                  required
                  min="1"
                  placeholder="e.g., 25"
                />
              </div>
              <div className="form-group">
                <label>Unit *</label>
                <select
                  value={formData.unit || 'miles'}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  required
                >
                  <option value="miles">Miles</option>
                  <option value="km">Kilometers</option>
                </select>
              </div>
              <div className="form-group">
                <label>Default Zoom (optional)</label>
                <input
                  type="number"
                  value={formData.default_zoom ?? ''}
                  onChange={(e) => setFormData({ ...formData, default_zoom: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                  min="10"
                  max="18"
                  step="0.1"
                  placeholder="(default is 13.00)"
                />
              </div>
              <div className="form-group">
                <label>Default pitch (optional, degrees 0–60)</label>
                <input
                  type="number"
                  value={formData.default_pitch ?? ''}
                  onChange={(e) => setFormData({ ...formData, default_pitch: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                  min="0"
                  max="60"
                  step="1"
                  placeholder="(default is 45)"
                />
              </div>
              <div className="form-group">
                <label>Default bearing (optional, degrees −360–360, clockwise from north)</label>
                <input
                  type="number"
                  value={formData.default_bearing ?? ''}
                  onChange={(e) => setFormData({ ...formData, default_bearing: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                  min="-360"
                  max="360"
                  step="1"
                  placeholder="e.g. 29 for Manhattan grid"
                />
              </div>
              <div className="form-group">
                <label>ZIP Code (optional, for Etix)</label>
                <input
                  type="text"
                  value={formData.zip_code || ''}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  placeholder="e.g., 60601"
                />
              </div>
              <div className="form-group">
                <label>Region background (optional)</label>
                {loadingBackgrounds ? (
                  <div>Loading CDN images…</div>
                ) : (
                  <select
                    value={formData.region_background || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, region_background: e.target.value || undefined })
                    }
                    disabled={
                      !((formData.region_name || '').trim() || (editingRegion?.region_name || '').trim())
                    }
                  >
                    <option value="">Default (bundled)</option>
                    {formData.region_background &&
                      !backgroundOptions.some((o) => o.value === formData.region_background) && (
                        <option value={formData.region_background}>
                          {formData.region_background} (current, not in list)
                        </option>
                      )}
                    {backgroundOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
                <small style={{ color: '#666', fontSize: '0.875rem' }}>
                  CDN folder <code>{REGION_BACKGROUND_CDN_FOLDER}/&lt;region_name&gt;/</code>. Public app uses{' '}
                  <code>/images/{REGION_BACKGROUND_CDN_FOLDER}/…</code>. Enter a region name above to list files.
                </small>
              </div>
              <div className="form-group">
                <label>Default map marker (optional)</label>
                {loadingBackgrounds ? (
                  <div>Loading CDN images…</div>
                ) : (
                  <select
                    value={formData.default_map_marker || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, default_map_marker: e.target.value || undefined })
                    }
                    disabled={
                      !((formData.region_name || '').trim() || (editingRegion?.region_name || '').trim())
                    }
                  >
                    <option value="">Global default</option>
                    {formData.default_map_marker &&
                      !markerOptions.some((o) => o.value === formData.default_map_marker) && (
                        <option value={formData.default_map_marker}>
                          {formData.default_map_marker} (current, not in list)
                        </option>
                      )}
                    {markerOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
                <small style={{ color: '#666', fontSize: '0.875rem' }}>
                  CDN folder <code>map-markers/&lt;region_name&gt;/</code>. Used for venues without a custom marker; their
                  name is shown beneath the pin.
                </small>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingRegion ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default RegionsPage;

